'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';
import NotificationHub from '@/components/NotificationHub';

const navLinks = [
  { label: 'Home', href: '/' },
  { label: 'Book a Round', href: '/online-booking' },
  { label: 'Facilities', href: '/#facilities' },
  { label: 'Events', href: '/online-booking#events' },
  { label: 'Membership', href: '/online-booking#membership' },
  { label: 'FAQ', href: '/faq' },
  { label: 'Contact', href: '/contact' },
];

export default function PublicNav() {
  const pathname = usePathname();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, signOut, isStaff } = useAuth();
  const router = useRouter();

  const handleSignOut = async () => {
    await signOut();
    router?.push('/');
    setMobileOpen(false);
  };

  return (
    <nav className="sticky top-0 z-50 bg-primary shadow-card-lg">
      <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-3 group">
            <AppLogo size={36} className="shrink-0" />
            <div className="flex flex-col leading-none">
              <span className="text-white font-bold text-base tracking-tight">
                Maritzburg
              </span>
              <span className="text-accent text-xs font-medium tracking-widest uppercase">
                Golf Club
              </span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks?.map((link) => (
              <Link
                key={`nav-${link?.href}`}
                href={link?.href}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150 ${
                  pathname === link?.href
                    ? 'text-accent bg-white/10' :'text-white/80 hover:text-white hover:bg-white/10'
                }`}
              >
                {link?.label}
              </Link>
            ))}
          </div>

          {/* Right Actions */}
          <div className="flex items-center gap-3">
            {user ? (
              <>
                <NotificationHub />
                {isStaff ? (
                  <Link
                    href="/administration-dashboard"
                    className="hidden md:flex items-center gap-1.5 text-white/80 hover:text-white text-sm font-medium transition-colors duration-150"
                  >
                    <Icon name="Cog6ToothIcon" size={16} variant="outline" />
                    Dashboard
                  </Link>
                ) : (
                  <>
                    <Link
                      href="/member-profile"
                      className="hidden md:flex items-center gap-1.5 text-white/80 hover:text-white text-sm font-medium transition-colors duration-150"
                    >
                      <Icon name="UserCircleIcon" size={16} variant="outline" />
                      My Profile
                    </Link>
                    <Link
                      href="/member-dashboard"
                      className="hidden md:flex items-center gap-1.5 text-white/80 hover:text-white text-sm font-medium transition-colors duration-150"
                    >
                      <Icon name="Squares2X2Icon" size={16} variant="outline" />
                      Dashboard
                    </Link>
                  </>
                )}
                <button
                  onClick={handleSignOut}
                  className="hidden md:flex items-center gap-1.5 text-white/60 hover:text-white text-sm font-medium transition-colors duration-150"
                >
                  <Icon name="ArrowRightOnRectangleIcon" size={16} variant="outline" />
                  Sign Out
                </button>
              </>
            ) : (
              <Link
                href="/visitor-registration-sign-up-login"
                className="hidden md:flex items-center gap-1.5 text-white/80 hover:text-white text-sm font-medium transition-colors duration-150"
              >
                <Icon name="UserIcon" size={16} variant="outline" />
                Sign In
              </Link>
            )}
            <Link
              href="/online-booking"
              className="btn-gold px-4 py-2 rounded-lg text-sm hidden md:block"
            >
              Book Now
            </Link>
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="md:hidden p-2 rounded-md text-white/80 hover:text-white hover:bg-white/10 transition-colors"
              aria-label="Toggle menu"
            >
              <Icon name={mobileOpen ? 'XMarkIcon' : 'Bars3Icon'} size={22} variant="outline" />
            </button>
          </div>
        </div>
      </div>
      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden bg-primary border-t border-white/10 fade-in">
          <div className="px-4 py-3 space-y-1">
            {navLinks?.map((link) => (
              <Link
                key={`mobile-nav-${link?.href}`}
                href={link?.href}
                onClick={() => setMobileOpen(false)}
                className="block px-3 py-2.5 rounded-md text-sm font-medium text-white/80 hover:text-white hover:bg-white/10 transition-colors"
              >
                {link?.label}
              </Link>
            ))}
            <div className="pt-2 border-t border-white/10 mt-2 space-y-2">
              {user ? (
                <>
                  {isStaff ? (
                    <Link
                      href="/administration-dashboard"
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-white/80 hover:text-white rounded-md hover:bg-white/10 transition-colors"
                    >
                      <Icon name="Cog6ToothIcon" size={16} variant="outline" />
                      Admin Dashboard
                    </Link>
                  ) : (
                    <>
                      <Link
                        href="/member-profile"
                        onClick={() => setMobileOpen(false)}
                        className="flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-white/80 hover:text-white rounded-md hover:bg-white/10 transition-colors"
                      >
                        <Icon name="UserCircleIcon" size={16} variant="outline" />
                        My Profile
                      </Link>
                      <Link
                        href="/member-dashboard"
                        onClick={() => setMobileOpen(false)}
                        className="flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-white/80 hover:text-white rounded-md hover:bg-white/10 transition-colors"
                      >
                        <Icon name="Squares2X2Icon" size={16} variant="outline" />
                        My Dashboard
                      </Link>
                    </>
                  )}
                  <button
                    onClick={handleSignOut}
                    className="flex items-center gap-2 w-full px-3 py-2.5 text-sm font-medium text-white/60 hover:text-white rounded-md hover:bg-white/10 transition-colors"
                  >
                    <Icon name="ArrowRightOnRectangleIcon" size={16} variant="outline" />
                    Sign Out
                  </button>
                </>
              ) : (
                <Link
                  href="/visitor-registration-sign-up-login"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-white/80 hover:text-white rounded-md hover:bg-white/10 transition-colors"
                >
                  <Icon name="UserIcon" size={16} variant="outline" />
                  Sign In / Register
                </Link>
              )}
              <Link
                href="/online-booking"
                onClick={() => setMobileOpen(false)}
                className="btn-gold w-full text-center px-4 py-2.5 rounded-lg text-sm block"
              >
                Book Now
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}